import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

// Define types for our state
export interface FilterState {
  category: number[];
  tag: number[];
  search: string;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  perPage: number; // Add perPage
}

export interface UIState {
  activeView: "grid" | "list";
}

// Define types for our action payloads for tags
interface TagActionPayload {
  action?: "clear";
  replace?: number[];
}

export interface ProductState {
  filters: FilterState;
  pagination: PaginationState;
  ui: UIState;
}

// Define types for our action payloads
interface CategoryActionPayload {
  action?: "clear";
  replace?: number[];
}

const initialState: ProductState = {
  // Current filter state
  filters: {
    category: [],
    tag: [],
    search: "",
  },
  // Active pagination
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    perPage: 12, // Default to 12 products per page
  },
  // UI state
  ui: {
    activeView: "grid", // 'grid' or 'list'
  },
};

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      // Reset to page 1 when filters change
      state.pagination.currentPage = 1;
    },
    setCategory: (
      state,
      action: PayloadAction<number | CategoryActionPayload>
    ) => {
      // Check if this is a clear action
      if (
        typeof action.payload === "object" &&
        action.payload.action === "clear"
      ) {
        // Clear all category filters
        state.filters.category = [];
      }
      // Check if this is a replace action with whole array
      else if (typeof action.payload === "object" && action.payload.replace) {
        // Replace the entire category array
        state.filters.category = action.payload.replace;
      } else {
        const category = action.payload as number;

        // If category is already in the array, remove it (toggle behavior)
        // This allows toggling both parent and child categories independently
        if (state.filters.category.includes(category)) {
          state.filters.category = state.filters.category.filter(
            (cat) => cat !== category
          );
        } else {
          // Otherwise add it to the array of selected categories
          // This maintains multiple selected categories for hierarchical navigation
          state.filters.category = [...state.filters.category, category];
        }
      }

      // Reset to page 1 when category changes
      state.pagination.currentPage = 1;
    },
    setTag: (state, action: PayloadAction<number | TagActionPayload>) => {
      // Check if this is a clear action
      if (
        typeof action.payload === "object" &&
        action.payload.action === "clear"
      ) {
        state.filters.tag = [];
      }
      // Check if this is a replace action with whole array
      else if (typeof action.payload === "object" && action.payload.replace) {
        state.filters.tag = action.payload.replace;
      } else {
        const tag = action.payload as number;
        // If tag is already in the array, remove it (toggle behavior)
        if (state.filters.tag.includes(tag)) {
          state.filters.tag = state.filters.tag.filter((t) => t !== tag);
        } else {
          // Otherwise add it
          state.filters.tag = [...state.filters.tag, tag];
        }
      }

      // Reset to page 1 when tag changes
      state.pagination.currentPage = 1;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.currentPage = 1; // Reset page on new search
    },
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.perPage = action.payload;
      state.pagination.currentPage = 1; // Reset to page 1 when perPage changes
    },
    setActiveView: (state, action: PayloadAction<"grid" | "list">) => {
      state.ui.activeView = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
  },
});

export const {
  setFilters,
  setCategory,
  setTag,
  setSearch,
  setPagination,
  setPage,
  setPerPage, // Export the new action
  setActiveView,
  resetFilters,
} = productSlice.actions;

export default productSlice.reducer;
